import { FC, useState } from "react"
import RadioButton from "@/components/shared/RadioButton"
import { TestId } from "@/constants/globals"
import { CardSize, Token } from "@/types/index"
import { capitalize } from "@/utils/lodash"

export enum CardType {
   CELL = "cell",
   INMATE = "inmate",
}

interface CardSizeSettingProps {
   cardType: CardType
}

type CardSizeTestIdKey =
   `${Uppercase<CardType>}_CARD_SIZE_${Uppercase<CardSize>}_RADIO`

const getCardSizeTestId = (cardType: CardType, cardSize: CardSize): TestId =>
   TestId[
      `${cardType.toUpperCase()}_CARD_SIZE_${cardSize.toUpperCase()}_RADIO` as CardSizeTestIdKey
   ]

const cardSizeOptions = [
   { label: "Small", value: CardSize.SMALL },
   { label: "Medium", value: CardSize.MEDIUM },
   { label: "Large", value: CardSize.LARGE },
]

const cardSizeStorage: Record<
   CardType,
   { token: Token; defaultSize: CardSize }
> = {
   [CardType.CELL]: {
      token: Token.CELL_CARD_SIZE,
      defaultSize: CardSize.LARGE,
   },
   [CardType.INMATE]: {
      token: Token.INMATE_CARD_SIZE,
      defaultSize: CardSize.MEDIUM,
   },
}

const CardSizeSetting: FC<CardSizeSettingProps> = ({ cardType }) => {
   const { token, defaultSize } = cardSizeStorage[cardType]
   const [value, setValue] = useState<CardSize>(
      () => (localStorage.getItem(token) as CardSize | null) ?? defaultSize
   )

   const onChange = (cardSize: CardSize): void => {
      setValue(cardSize)
      localStorage.setItem(token, cardSize)
   }

   return (
      <div className="preferences__section">
         <div className="preferences__section-title">{`${capitalize(cardType)} Card Size`}</div>
         <div className="preferences__section-subtitle">{`Choose the ${cardType} card size`}</div>
         <div className="preferences__radio-group">
            {cardSizeOptions.map((option) => (
               <RadioButton
                  key={option.value}
                  id={`${cardType}-card-size-setting-${option.value}`}
                  name={`${cardType}-card-size-setting`}
                  label={option.label}
                  value={option.value}
                  checked={value === option.value}
                  onChange={() => onChange(option.value)}
                  testId={getCardSizeTestId(cardType, option.value)}
               />
            ))}
         </div>
      </div>
   )
}

export default CardSizeSetting
